import { useCallback, useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useUserData } from '@nhost/react'
import { graphql } from '@/shared/graphql/generated'
import type { ReadingPrefs } from '@/shared/lib/sectionTopics'

/**
 * Persönliche Präferenz, Einleitung- und/oder Ende-Abschnitte beim Lesen/Hören zu
 * überspringen. Liegt in `user_profiles`, wie `paid` oder `completed_chapters` - eine
 * Apollo-`useQuery` statt eigenem `nhost.graphql.request` je Aufrufer, damit Profil,
 * Leseansicht und Kapitel-Listen dieselbe Cache-Zeile teilen und ein Toggle im Profil
 * überall sofort greift, ohne dass jede Stelle einzeln neu lädt.
 */

const GET_READING_PREFERENCES = graphql(`
  query GetReadingPreferences($userId: uuid!) {
    user_profiles(where: { user_id: { _eq: $userId } }, limit: 1) {
      id
      hide_intro_sections
      hide_outro_sections
    }
  }
`)

const SET_READING_PREFERENCES = graphql(`
  mutation SetReadingPreferences($userId: uuid!, $hideIntro: Boolean!, $hideOutro: Boolean!) {
    insert_user_profiles_one(
      object: { user_id: $userId, hide_intro_sections: $hideIntro, hide_outro_sections: $hideOutro }
      on_conflict: {
        constraint: user_profiles_user_id_key
        update_columns: [hide_intro_sections, hide_outro_sections]
      }
    ) {
      id
      hide_intro_sections
      hide_outro_sections
    }
  }
`)

export function useReadingPreferences() {
  const user = useUserData()
  const userId = user?.id

  const { data, loading } = useQuery(GET_READING_PREFERENCES, {
    variables: { userId: userId ?? '' },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  })

  const [setPreferencesMutation] = useMutation(SET_READING_PREFERENCES)

  const row = data?.user_profiles?.[0]
  const prefs: Required<ReadingPrefs> = useMemo(
    () => ({
      hideIntroSections: row?.hide_intro_sections ?? false,
      hideOutroSections: row?.hide_outro_sections ?? false,
    }),
    [row?.hide_intro_sections, row?.hide_outro_sections]
  )

  const setReadingPrefs = useCallback(
    async (next: Partial<ReadingPrefs>) => {
      if (!userId) return
      try {
        await setPreferencesMutation({
          variables: {
            userId,
            hideIntro: next.hideIntroSections ?? prefs.hideIntroSections,
            hideOutro: next.hideOutroSections ?? prefs.hideOutroSections,
          },
          optimisticResponse: {
            insert_user_profiles_one: {
              __typename: 'user_profiles',
              id: row?.id ?? 'temp-reading-prefs',
              hide_intro_sections: next.hideIntroSections ?? prefs.hideIntroSections,
              hide_outro_sections: next.hideOutroSections ?? prefs.hideOutroSections,
            },
          },
        })
      } catch (err) {
        // Apollo rollt den optimistischen Wert bei einem Fehler zurück - ohne dieses
        // Log sähe man nur, dass die Checkbox wieder umspringt, aber nicht warum
        // (typischerweise eine fehlende Hasura-Update-Permission auf der Spalte).
        console.error('Failed to save reading preferences:', err)
      }
    },
    [userId, prefs.hideIntroSections, prefs.hideOutroSections, row?.id, setPreferencesMutation]
  )

  return { ...prefs, loading, setReadingPrefs }
}
